<template>
  <controller :goToTopButton="true" :lockScroll="showExecutionConfirmation">
    <main class="enrollment-import-page">
      <header class="page-heading">
        <div>
          <span class="eyebrow">Matrículas / Integração de unidades</span>
          <h1>Importação de matrículas</h1>
          <p>Analise a planilha contra Apollo, Perseus e Google antes de autorizar qualquer criação.</p>
        </div>
        <span class="analysis-only-badge">Análise e execução controlada</span>
      </header>

      <section class="upload-panel" aria-labelledby="upload-title">
        <div class="section-heading">
          <div>
            <span class="step">1</span>
            <div>
              <h2 id="upload-title">Enviar planilha</h2>
              <p>Utilize o template XLSX de migração. A análise relê matrículas ativas e a capacidade atual das turmas.</p>
            </div>
          </div>
          <button v-if="report" type="button" class="btn btn-secondary" :disabled="analyzing || executionInProgress" @click="reset">
            Analisar outro arquivo
          </button>
        </div>

        <label
          v-if="!report"
          class="drop-zone"
          :class="{ dragging, disabled: analyzing }"
          @dragenter.prevent="dragging = true"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="handleDrop"
          @keydown.enter.prevent="$refs.fileInput.click()"
          @keydown.space.prevent="$refs.fileInput.click()"
          tabindex="0"
        >
          <input ref="fileInput" type="file" accept=".xlsx" :disabled="analyzing" @change="handleFile">
          <span class="upload-icon" aria-hidden="true"><fa :icon="['fas', 'file-spreadsheet']" /></span>
          <strong>{{ selectedFile ? selectedFile.name : 'Arraste o arquivo ou selecione no computador' }}</strong>
          <span>{{ selectedFile ? fileSize(selectedFile.size) : 'Somente arquivos .xlsx' }}</span>
        </label>

        <div v-if="!report" class="upload-actions">
          <div class="selected-file" v-if="selectedFile">
            <span>Arquivo selecionado</span>
            <strong>{{ selectedFile.name }}</strong>
          </div>
          <button type="button" class="btn btn-primary" :disabled="!selectedFile || analyzing" @click="analyze">
            {{ analyzing ? 'Analisando...' : 'Analisar planilha' }}
          </button>
        </div>

        <div v-if="analyzing" class="analysis-progress" role="status" aria-live="polite">
          <span class="progress-bar"><i></i></span>
          <div>
            <strong>{{ analysisStage }}</strong>
            <span>A página acompanha o processamento em segundo plano. Nenhuma matrícula será criada.</span>
          </div>
        </div>

        <div v-if="error" class="state-message error" role="alert">
          <div>
            <strong>{{ errorTitle }}</strong>
            <span>{{ error }}</span>
          </div>
          <button type="button" class="btn btn-secondary" @click="error = ''">Fechar</button>
        </div>
      </section>

      <template v-if="report">
        <section class="summary-section" aria-labelledby="summary-title">
          <div class="section-heading">
            <div>
              <span class="step">2</span>
              <div>
                <h2 id="summary-title">Resultado da análise</h2>
                <p>{{ report.import.fileName }} · SHA-256 {{ shortHash(report.import.fileHash) }}</p>
              </div>
            </div>
            <div class="analysis-meta">
              <span class="analyzed-at">{{ formatDate(report.import.analyzedAt) }}</span>
              <button
                type="button"
                class="btn btn-secondary feedback-download"
                :disabled="downloadingFeedback || !report.import.feedbackAvailable"
                :aria-describedby="!report.import.feedbackAvailable ? 'feedback-unavailable' : null"
                @click="downloadFeedback"
              >
                <fa :icon="['fas', 'file-spreadsheet']" aria-hidden="true" />
                {{ downloadingFeedback ? 'Preparando download...' : 'Baixar planilha com problemas' }}
              </button>
              <small v-if="!report.import.feedbackAvailable" id="feedback-unavailable" class="feedback-note">
                Reenvie esta planilha uma vez para gerar a devolutiva anotada.
              </small>
              <small v-if="downloadError" class="feedback-error" role="alert">{{ downloadError }}</small>
            </div>
          </div>

          <div class="summary-grid" aria-live="polite">
            <button type="button" :class="['summary-item', { active: filters.status === '' }]" @click="setStatus('')">
              <span>Total</span><strong>{{ report.summary.total }}</strong><small>Todas as linhas</small>
            </button>
            <button type="button" :class="['summary-item ready', { active: filters.status === 'ready' }]" @click="setStatus('ready')">
              <span>Aptos</span><strong>{{ report.summary.ready }}</strong><small>Sem divergências</small>
            </button>
            <button type="button" :class="['summary-item structure', { active: filters.status === 'structure_creatable' }]" @click="setStatus('structure_creatable')">
              <span>Criação planejada</span><strong>{{ report.summary.structureCreatable }}</strong><small>Cadastros ou vínculos ausentes</small>
            </button>
            <button type="button" :class="['summary-item imported', { active: filters.status === 'already_imported' }]" @click="setStatus('already_imported')">
              <span>Já importadas</span><strong>{{ report.summary.alreadyImported || 0 }}</strong><small>Concluídas no Apollo, Perseus e Google</small>
            </button>
            <button type="button" :class="['summary-item blocked', { active: filters.status === 'blocked' }]" @click="setStatus('blocked')">
              <span>Bloqueados</span><strong>{{ report.summary.blocked }}</strong><small>Correção obrigatória</small>
            </button>
          </div>

          <div v-if="allRowsAlreadyImported" class="execution-panel completed-panel">
            <span class="execution-icon" aria-hidden="true"><fa :icon="['fas', 'check-circle']" /></span>
            <div class="execution-copy">
              <strong>Lote já reconciliado</strong>
              <p>Todas as linhas já constam como concluídas no Apollo, Perseus e Google. Nenhuma nova integração será enviada.</p>
            </div>
          </div>

          <div v-else-if="!report.executionEnabled && !executionInProgress && !hasExecutionResult" class="execution-lock">
            <span aria-hidden="true"><fa :icon="['fas', 'lock']" /></span>
            <div>
              <strong>Esta análise ainda não pode ser executada</strong>
              <p>Reenvie a planilha depois da publicação do executor ou conclua os pré-requisitos acadêmicos indicados.</p>
            </div>
          </div>

          <div v-else class="execution-panel" role="status" aria-live="polite">
            <span class="execution-icon" aria-hidden="true"><fa :icon="['fas', executionInProgress ? 'spinner' : 'check-circle']" :spin="executionInProgress" /></span>
            <div class="execution-copy">
              <strong>{{ executionTitle }}</strong>
              <p>{{ executionDescription }}</p>
              <ul>
                <li>{{ eligibleCount }} linha(s) ainda não enviada(s); {{ failedRows.length }} falha(s) podem ser corrigidas e reenviadas depois; {{ report.summary.blocked }} bloqueada(s) serão excluídas.</li>
                <li>Nenhum plano de pagamento, mensalidade ou cobrança será criado.</li>
                <li v-if="hasGooglePrerequisite">Contas Google ficarão pendentes até o caminho organizacional da unidade estar confirmado.</li>
              </ul>
            </div>
            <div class="execution-actions">
              <button v-if="report.executionEnabled && eligibleCount && !executionInProgress && !executionUncertain" ref="executionTrigger" type="button" class="btn btn-primary" @click="openExecutionConfirmation()">
                {{ hasExecutionResult ? `Continuar com ${eligibleCount} linha(s)` : `Revisar e integrar ${eligibleCount}` }}
              </button>
              <button v-if="report.executionEnabled && failedRows.length && !executionInProgress && !executionUncertain" type="button" class="btn btn-secondary" @click="openExecutionConfirmation(true)">
                Tentar novamente {{ failedRows.length }} linha(s) com falha
              </button>
              <button v-if="executionInProgress || hasExecutionResult || executionUncertain" type="button" class="btn btn-secondary" :disabled="pollingExecution" @click="refreshExecution">
                {{ pollingExecution ? 'Atualizando...' : 'Atualizar andamento' }}
              </button>
            </div>
          </div>

          <p v-if="executionNotice" class="execution-notice" role="status">{{ executionNotice }}</p>
          <ul v-if="executedRows.length" class="execution-results" aria-label="Resultado das linhas enviadas">
            <li v-for="row in executedRows" :key="row.sourceRow">
              <strong>L{{ row.sourceRow }} · {{ executionRowLabel(row.executionStatus) }}</strong>
              <span v-if="row.executionStatus === 'succeeded'">Concluída no Apollo, Perseus e Google.</span>
              <span v-if="rowExecutionError(row)" class="execution-error">{{ rowExecutionError(row) }}</span>
              <small v-if="rowExecutionErrorReference(row)">Referência {{ rowExecutionErrorReference(row) }}</small>
            </li>
          </ul>

          <div v-if="hasSharedAnalysis" class="shared-analysis">
            <div class="shared-heading">
              <div>
                <span class="shared-kicker">Executar uma vez</span>
                <h3>Pré-requisitos compartilhados do lote</h3>
                <p>Google e estrutura acadêmica ficam fora das linhas. Depois de criados, são reutilizados pelas matrículas indicadas.</p>
              </div>
              <span class="shared-count">{{ sharedAnalysis.creationPlan.length }} ação(ões)</span>
            </div>

            <div class="shared-grid">
              <section>
                <h4>Validações da estrutura</h4>
                <ol v-if="sharedAnalysis.searchTrace.length" class="trace-list shared-list">
                  <li v-for="(trace, index) in sharedAnalysis.searchTrace" :key="`shared-trace-${index}`">
                    <div class="trace-header">
                      <strong>{{ trace.system }} · {{ trace.entity }}<small>{{ trace.scope }}</small></strong>
                      <span :class="['trace-status', trace.status]">{{ traceStatusLabel(trace.status) }}</span>
                    </div>
                    <p>{{ trace.result }}</p>
                    <small class="affected-rows">Linhas {{ formatRows(trace.appliesToRows) }}</small>
                  </li>
                </ol>
                <p v-else class="no-issues">Nenhuma validação compartilhada registrada.</p>
              </section>

              <section>
                <h4>Divergências compartilhadas</h4>
                <ul v-if="sharedAnalysis.issues.length" class="issue-list shared-list">
                  <li v-for="(issue, index) in sharedAnalysis.issues" :key="`shared-issue-${index}`" :class="issue.severity">
                    <div class="issue-title"><span>{{ issueLabel(issue.code) }}</span><small>{{ issue.scope }}</small></div>
                    <p>{{ issue.message }}</p>
                    <p v-if="issue.resolution" class="resolution"><strong>Como resolver:</strong> {{ issue.resolution }}</p>
                    <small class="affected-rows">Linhas {{ formatRows(issue.appliesToRows) }}</small>
                  </li>
                </ul>
                <p v-else class="no-issues">Nenhuma divergência compartilhada.</p>
              </section>

              <section>
                <h4>Plano único de criação</h4>
                <ol v-if="sharedAnalysis.creationPlan.length" class="structure-list shared-list">
                  <li v-for="(action, index) in sharedAnalysis.creationPlan" :key="`shared-action-${index}`">
                    <div class="action-heading"><span>{{ index + 1 }}</span><strong>{{ action.system }} · {{ creationLabel(action) }}</strong></div>
                    <small class="scope-label">{{ action.scope }}</small>
                    <p>{{ action.description }}</p>
                    <div v-if="(action.prerequisites || []).length" class="prerequisites">
                      <small>Pré-requisitos</small>
                      <ul><li v-for="item in action.prerequisites" :key="item">{{ item }}</li></ul>
                    </div>
                    <small class="affected-rows">Atende às linhas {{ formatRows(action.appliesToRows) }}</small>
                  </li>
                </ol>
                <p v-else class="no-issues">Nenhuma criação compartilhada necessária.</p>
              </section>
            </div>
          </div>
        </section>

        <section class="results-section" aria-labelledby="results-title">
          <div class="section-heading results-heading">
            <div>
              <span class="step">3</span>
              <div>
                <h2 id="results-title">Linhas da planilha</h2>
                <p>{{ filteredRows.length }} resultado(s) com os filtros atuais.</p>
              </div>
            </div>
          </div>

          <form class="filters" @submit.prevent>
            <label class="search-field">
              <span>Buscar aluno ou ID</span>
              <input v-model.trim="filters.search" type="search" placeholder="Nome ou ID de origem" autocomplete="off" @input="page = 1">
            </label>
            <label>
              <span>Situação</span>
              <select v-model="filters.status" @change="page = 1">
                <option value="">Todas</option>
                <option value="ready">Apto</option>
                <option value="structure_creatable">Criação planejada</option>
                <option value="already_imported">Já importada</option>
                <option value="blocked">Bloqueado</option>
              </select>
            </label>
            <label>
              <span>Turma</span>
              <select v-model="filters.classCode" @change="page = 1">
                <option value="">Todas</option>
                <option v-for="classCode in classOptions" :key="classCode" :value="classCode">{{ classCode }}</option>
              </select>
            </label>
            <label>
              <span>Divergência</span>
              <select v-model="filters.issueCode" @change="page = 1">
                <option value="">Todas</option>
                <option v-for="issue in issueOptions" :key="issue.code" :value="issue.code">{{ issue.label }} ({{ issue.count }})</option>
              </select>
            </label>
            <button type="button" class="btn btn-secondary" :disabled="!hasFilters" @click="clearFilters">Limpar filtros</button>
          </form>

          <div v-if="!pagedRows.length" class="empty-state">
            <strong>Nenhuma linha encontrada</strong>
            <span>Altere ou limpe os filtros para visualizar outros resultados.</span>
          </div>

          <div v-else class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Linha / ID</th>
                  <th>Aluno</th>
                  <th>Unidade / ano</th>
                  <th>Série / turma</th>
                  <th>Situação</th>
                  <th><span class="sr-only">Detalhes</span></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="row in pagedRows" :key="row.sourceRow">
                  <tr>
                    <td><strong>L{{ row.sourceRow }}</strong><small>ID {{ row.sourceId || '-' }}</small></td>
                    <td><strong>{{ row.studentName }}</strong><small>{{ issueSummary(row) }}</small></td>
                    <td><strong>{{ row.unit }}</strong><small>{{ row.academicYear }}</small></td>
                    <td><strong>{{ row.series }}</strong><small>{{ row.class }}</small></td>
                    <td>
                      <span :class="['status-badge', row.status]">{{ statusLabel(row.status) }}</span>
                      <small v-if="row.executionStatus && row.executionStatus !== 'not_requested'" :class="['execution-row-status', row.executionStatus]">{{ executionRowLabel(row.executionStatus) }}</small>
                    </td>
                    <td class="details-cell">
                      <button type="button" class="detail-button" :aria-expanded="isExpanded(row)" :aria-controls="`details-${row.sourceRow}`" @click="toggleRow(row)">
                        {{ isExpanded(row) ? 'Ocultar' : 'Detalhes' }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="isExpanded(row)" :id="`details-${row.sourceRow}`" class="detail-row">
                    <td colspan="6">
                      <div class="detail-grid">
                        <section class="detail-section search-section">
                          <div class="detail-heading">
                            <span class="detail-step">1</span>
                            <div><h3>Rastreio por pessoa</h3><p>Critérios consultados para aluno e responsáveis.</p></div>
                          </div>
                          <ol v-if="(row.searchTrace || []).length" class="trace-list">
                            <li v-for="(trace, index) in (row.searchTrace || [])" :key="`${trace.system}-${trace.entity}-${trace.role}-${index}`">
                              <div class="trace-header">
                                <strong>{{ trace.system }} · {{ trace.entity }}<small v-if="trace.role">{{ roleLabel(trace.role) }}</small></strong>
                                <span :class="['trace-status', trace.status]">{{ traceStatusLabel(trace.status) }}</span>
                              </div>
                              <ul><li v-for="criterion in trace.criteria" :key="criterion">{{ criterion }}</li></ul>
                              <p>{{ trace.result }}</p>
                            </li>
                          </ol>
                          <p v-else class="no-issues">Esta análise antiga não possui rastreio detalhado. Reenvie a planilha.</p>
                        </section>
                        <section class="detail-section issue-section">
                          <div class="detail-heading">
                            <span class="detail-step">2</span>
                            <div><h3>Divergências da linha</h3><p>Dados pessoais que precisam ser corrigidos ou confirmados.</p></div>
                          </div>
                          <ul v-if="row.issues.length" class="issue-list">
                            <li v-for="(issue, index) in row.issues" :key="`${issue.code}-${index}`" :class="issue.severity">
                              <div class="issue-title"><span>{{ issueLabel(issue.code) }}</span><small>{{ issue.field }}</small></div>
                              <p>{{ issue.message }}</p>
                              <p v-if="issue.resolution" class="resolution"><strong>Como resolver:</strong> {{ issue.resolution }}</p>
                            </li>
                          </ul>
                          <p v-else class="no-issues">Nenhuma divergência encontrada.</p>
                        </section>
                        <section class="detail-section creation-section">
                          <div class="detail-heading">
                            <span class="detail-step">3</span>
                            <div><h3>Plano por pessoa</h3><p>Pessoas e usuários ausentes no Apollo ou no Perseus.</p></div>
                          </div>
                          <ol v-if="(row.creationPlan || []).length" class="structure-list">
                            <li v-for="(action, index) in (row.creationPlan || [])" :key="`${action.type}-${action.role}-${index}`">
                              <div class="action-heading"><span>{{ index + 1 }}</span><strong>{{ action.system }} · {{ creationLabel(action) }}</strong></div>
                              <p>{{ action.description }}</p>
                              <div v-if="(action.prerequisites || []).length" class="prerequisites">
                                <small>Pré-requisitos</small>
                                <ul><li v-for="item in action.prerequisites" :key="item">{{ item }}</li></ul>
                              </div>
                            </li>
                          </ol>
                          <p v-else class="no-issues">Nenhuma criação ou vínculo necessário.</p>
                          <div v-if="row.executionStatus && row.executionStatus !== 'not_requested'" class="row-execution-result">
                            <strong>Execução: {{ executionRowLabel(row.executionStatus) }}</strong>
                            <p v-if="row.executionResult?.enrollmentId">Matrícula Apollo {{ row.executionResult.enrollmentId }} · Aluno {{ row.executionResult.studentId }}</p>
                            <p v-if="rowExecutionError(row)" class="execution-error">{{ rowExecutionError(row) }}</p>
                            <small v-if="rowExecutionErrorReference(row)">Referência {{ rowExecutionErrorReference(row) }}</small>
                          </div>
                        </section>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <nav v-if="totalPages > 1" class="pagination" aria-label="Paginação do relatório">
            <button type="button" class="btn btn-secondary" :disabled="page === 1" @click="page--">Anterior</button>
            <span>Página {{ page }} de {{ totalPages }}</span>
            <button type="button" class="btn btn-secondary" :disabled="page === totalPages" @click="page++">Próxima</button>
          </nav>
        </section>
      </template>

      <Teleport to="body">
        <div v-if="showExecutionConfirmation" class="enrollment-execution-overlay" @click.self="closeExecutionConfirmation">
          <section ref="executionDialog" class="execution-dialog" role="dialog" aria-modal="true" aria-labelledby="execution-dialog-title" tabindex="-1" @keydown="handleDialogKeydown">
            <span class="dialog-kicker">Confirmação obrigatória</span>
            <h2 id="execution-dialog-title">{{ retryFailedOnly ? 'Tentar novamente linhas com falha' : 'Autorizar integração' }}</h2>
            <p>O Apollo criará os cadastros locais e enviará as matrículas ao Perseus. As {{ report.summary.blocked }} linhas bloqueadas não serão processadas.</p>

            <label class="canary-option">
              <input v-model="canaryOnly" type="checkbox" :disabled="executing" @change="executionConfirmation = ''">
              <span><strong>Testar uma linha primeiro</strong><small>Processa somente {{ canaryRowLabel }}. Desmarque para enviar até 500 das {{ executionCandidateRows.length }} linhas desta seleção. Se restarem linhas, use “Continuar” depois.</small></span>
            </label>

            <div class="confirmation-summary">
              <span>Linhas desta execução</span><strong>{{ executionSelectionCount }}</strong>
              <span>Confirmação</span><strong>{{ expectedConfirmation }}</strong>
            </div>

            <label class="confirmation-field">
              <span>Digite exatamente <strong>{{ expectedConfirmation }}</strong></span>
              <input ref="confirmationInput" v-model.trim="executionConfirmation" type="text" autocomplete="off" :disabled="executing" aria-describedby="execution-confirmation-help">
              <small id="execution-confirmation-help">{{ confirmationMatches ? 'Quantidade confirmada. A integração pode ser enviada.' : `O botão será habilitado ao digitar ${expectedConfirmation}.` }}</small>
            </label>

            <div v-if="executionError" class="dialog-error" role="alert">{{ executionError }}</div>
            <div class="dialog-actions">
              <button type="button" class="btn btn-secondary" :disabled="executing" @click="closeExecutionConfirmation">Cancelar</button>
              <button type="button" class="btn btn-primary" :disabled="executing || !confirmationMatches" @click="executeImport">
                {{ executing ? 'Enfileirando...' : `Integrar ${executionSelectionCount} linha(s)` }}
              </button>
            </div>
          </section>
        </div>
      </Teleport>
    </main>
  </controller>
</template>

<script>
import EnrollmentImportService from '@/services/enrollment-import.service'

const issueLabels = {
  DUPLICATE_SOURCE_ID: 'ID de origem duplicado',
  SOURCE_ID_IDENTITY_CONFLICT: 'ID associado a alunos diferentes',
  DUPLICATE_ENROLLMENT_IN_FILE: 'Matrícula repetida na planilha',
  APOLLO_PERSON_REUSED_WITHOUT_BIRTH_DATE: 'Cadastro reutilizado no Apollo sem nascimento',
  PERSEUS_PERSON_REUSED_WITHOUT_BIRTH_DATE: 'Cadastro reutilizado no Perseus sem nascimento',
  CPF_IDENTITY_CONFLICT: 'CPF associado a identidades diferentes',
  LOGIN_EMAIL_CONFLICT: 'E-mail compartilhado por responsáveis',
  PERSON_NAME_REQUIRED: 'Nome obrigatório',
  PERSON_BIRTH_DATE_INVALID: 'Nascimento inválido',
  PERSON_SEX_INVALID: 'Sexo inválido',
  PERSON_CPF_INVALID: 'CPF inválido',
  PERSON_EMAIL_INVALID: 'E-mail inválido',
  APOLLO_PERSON_IDENTITY_CONFLICT: 'Conflito de pessoa no Apollo',
  APOLLO_PERSON_CANDIDATE_REVIEW: 'Possível pessoa duplicada no Apollo',
  APOLLO_USER_EMAIL_CONFLICT: 'Conflito de usuário no Apollo',
  PERSEUS_PERSON_EXISTS: 'Pessoa existente no Perseus',
  PERSEUS_PERSON_IDENTITY_CONFLICT: 'Conflito de identidade no Perseus',
  PERSEUS_PERSON_CANDIDATE_REVIEW: 'Possível pessoa duplicada no Perseus',
  PERSEUS_EMAIL_DIVERGES: 'E-mail diferente no Perseus',
  PERSEUS_UNAVAILABLE: 'Perseus indisponível',
  PERSEUS_STRUCTURE_UNAVAILABLE: 'Estrutura Perseus indisponível',
  APOLLO_UNIT_NOT_FOUND: 'Unidade ausente no Apollo',
  UNIT_PERSEUS_CODE_MISSING: 'SEDCodigo não configurado',
  GOOGLE_ORG_UNIT_MISSING: 'Organização Google ausente',
  APOLLO_PERIOD_NOT_FOUND: 'Período ausente no Apollo',
  APOLLO_COURSE_NOT_FOUND: 'Curso ausente no Apollo',
  APOLLO_SHIFT_NOT_FOUND: 'Turno ausente no Apollo',
  APOLLO_SERIES_NOT_FOUND: 'Série ausente no Apollo',
  APOLLO_CLASS_NOT_FOUND: 'Turma ausente no Apollo',
  APOLLO_SERIES_TEMPLATE_NOT_FOUND: 'Série sem unidade-modelo',
  APOLLO_CLASS_TEMPLATE_NOT_FOUND: 'Turma sem unidade-modelo',
  PERSEUS_CLASS_NOT_FOUND: 'Turma ausente no Perseus',
  PERSEUS_CLASS_AMBIGUOUS: 'Turma ambígua no Perseus',
  SERIES_CODE_CONFLICT: 'SERCodigo divergente',
  CLASS_CAPACITY_ZERO: 'Turma com capacidade zero',
  CLASS_WITHOUT_CAPACITY: 'Turma sem vagas',
  BATCH_EXCEEDS_CLASS_CAPACITY: 'Lote excede a capacidade da turma',
  ENROLLMENT_ALREADY_IMPORTED: 'Matrícula já importada',
  ENROLLMENT_ALREADY_EXISTS_IN_APOLLO: 'Matrícula já existente no Apollo',
  PERSEUS_CLASS_ALREADY_LINKED: 'Turma Perseus já vinculada',
  PS_TURMAS_CONFLICT: 'Vínculo ps_turmas conflitante',
  PS_TURMAS_FIELDS_DIVERGE: 'Códigos ps_turmas divergentes',
  ACADEMIC_FIELD_REQUIRED: 'Dado acadêmico obrigatório',
  SOURCE_ID_REQUIRED: 'ID de origem obrigatório'
}

export default {
  name: 'EnrollmentImport',
  data () {
    return {
      selectedFile: null,
      dragging: false,
      analyzing: false,
      analysisStage: 'Enviando planilha para análise',
      analysisPollTimer: null,
      executionPollTimer: null,
      pollingExecution: false,
      executionNotice: '',
      executionUncertain: false,
      disposed: false,
      error: '',
      errorTitle: 'Não foi possível analisar a planilha',
      executionError: '',
      downloadError: '',
      downloadingFeedback: false,
      executing: false,
      showExecutionConfirmation: false,
      canaryOnly: true,
      retryFailedOnly: false,
      executionConfirmation: '',
      report: null,
      expandedRows: [],
      page: 1,
      perPage: 25,
      filters: { search: '', status: '', classCode: '', issueCode: '' }
    }
  },
  mounted () {
    const uuid = this.$route.query.import
    if (typeof uuid !== 'string' || !/^[a-f0-9-]{36}$/i.test(uuid)) return
    this.analyzing = true
    this.analysisStage = 'Recuperando análise e andamento da execução'
    this.pollAnalysis(uuid, 0)
  },
  beforeUnmount () {
    this.disposed = true
    this.clearAnalysisPoll()
    this.clearExecutionPoll()
  },
  computed: {
    sharedAnalysis () {
      return this.report?.summary?.sharedAnalysis || { issues: [], searchTrace: [], creationPlan: [] }
    },
    hasSharedAnalysis () {
      return this.sharedAnalysis.issues.length || this.sharedAnalysis.searchTrace.length || this.sharedAnalysis.creationPlan.length
    },
    eligibleRows () {
      return (this.report?.rows || []).filter(row => ['ready', 'structure_creatable'].includes(row.status) && row.executionStatus === 'not_requested')
    },
    failedRows () {
      return (this.report?.rows || []).filter(row => ['ready', 'structure_creatable'].includes(row.status) && row.executionStatus === 'failed')
    },
    eligibleCount () {
      return this.eligibleRows.length
    },
    executedRows () {
      return (this.report?.rows || []).filter(row => row.executionStatus && row.executionStatus !== 'not_requested')
    },
    confirmationMatches () {
      return this.executionSelectionCount > 0 && this.executionConfirmation.toLocaleUpperCase('pt-BR') === this.expectedConfirmation
    },
    allRowsAlreadyImported () {
      const total = this.report?.summary?.total || 0
      return total > 0 && (this.report?.summary?.alreadyImported || 0) === total
    },
    executionInProgress () {
      return ['queued', 'processing', 'integrating'].includes(this.report?.import?.executionStatus)
    },
    hasExecutionResult () {
      return ['completed', 'completed_with_errors', 'failed'].includes(this.report?.import?.executionStatus)
    },
    hasGooglePrerequisite () {
      return this.sharedAnalysis.creationPlan.some(action => action.type === 'create_google_org_unit')
    },
    executionSelectionCount () {
      return this.executionSelectionRows.length
    },
    executionCandidateRows () {
      return this.retryFailedOnly ? this.failedRows : this.eligibleRows
    },
    executionSelectionRows () {
      return this.executionCandidateRows.slice(0, this.canaryOnly ? 1 : 500)
    },
    expectedConfirmation () {
      return `INTEGRAR ${this.executionSelectionCount}`
    },
    canaryRowLabel () {
      return this.executionCandidateRows.length ? `a linha L${this.executionCandidateRows[0].sourceRow}` : 'uma linha elegível'
    },
    executionTitle () {
      const status = this.report?.import?.executionStatus
      return {
        queued: 'Execução aguardando processamento',
        processing: 'Criando cadastros locais',
        integrating: 'Concluindo integrações Perseus e Google',
        completed: 'Execução concluída',
        completed_with_errors: 'Execução concluída com falhas',
        failed: 'Falha no processamento do lote'
      }[status] || 'Execução disponível'
    },
    executionDescription () {
      const summary = this.report?.executionSummary || {}
      if (this.executionInProgress) return `${summary.succeeded || 0} concluída(s), ${summary.failed || 0} falha(s), ${summary.active ?? summary.requested ?? this.executionSelectionCount} em processamento. O andamento é atualizado automaticamente.`
      if (this.hasExecutionResult) return `${summary.succeeded || 0} concluída(s), ${summary.failed || 0} falha(s). ${this.eligibleCount ? `Restam ${this.eligibleCount} linha(s) ainda não enviada(s). Use “Continuar” para enviar o restante.` : (this.failedRows.length ? 'As linhas com falha podem ser corrigidas e reenviadas separadamente.' : 'Consulte o resultado abaixo.')}`
      return 'Revise o escopo e confirme a quantidade antes de criar pessoas, usuários e matrículas.'
    },
    classOptions () {
      return [...new Set((this.report?.rows || []).map(row => row.class).filter(Boolean))].sort()
    },
    issueOptions () {
      const counts = this.report?.summary?.issueCounts || {}
      return Object.entries(counts).map(([code, count]) => ({ code, count, label: this.issueLabel(code) }))
    },
    filteredRows () {
      const search = this.filters.search.toLocaleLowerCase('pt-BR')
      return (this.report?.rows || []).filter(row => {
        const matchesSearch = !search || row.studentName.toLocaleLowerCase('pt-BR').includes(search) || String(row.sourceId).includes(search)
        const matchesStatus = !this.filters.status || row.status === this.filters.status
        const matchesClass = !this.filters.classCode || row.class === this.filters.classCode
        const matchesIssue = !this.filters.issueCode || row.issues.some(issue => issue.code === this.filters.issueCode) || this.sharedIssuesForRow(row).some(issue => issue.code === this.filters.issueCode)
        return matchesSearch && matchesStatus && matchesClass && matchesIssue
      })
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.perPage))
    },
    pagedRows () {
      const safePage = Math.min(this.page, this.totalPages)
      const start = (safePage - 1) * this.perPage
      return this.filteredRows.slice(start, start + this.perPage)
    },
    hasFilters () {
      return Object.values(this.filters).some(Boolean)
    }
  },
  methods: {
    handleFile (event) {
      this.selectFile(event.target.files?.[0])
    },
    handleDrop (event) {
      this.dragging = false
      this.selectFile(event.dataTransfer.files?.[0])
    },
    selectFile (file) {
      this.error = ''
      if (!file) return
      if (!file.name.toLocaleLowerCase('pt-BR').endsWith('.xlsx')) {
        this.error = 'Selecione um arquivo no formato XLSX.'
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.error = 'O arquivo excede o limite de 10 MB.'
        return
      }
      this.selectedFile = file
    },
    analyze () {
      if (!this.selectedFile || this.analyzing) return
      this.analyzing = true
      this.analysisStage = 'Enviando planilha para análise'
      this.error = ''
      this.errorTitle = 'Não foi possível analisar a planilha'
      EnrollmentImportService.analyze(this.selectedFile)
        .then(response => {
          const payload = response.data
          if (payload.import.status === 'analyzed') {
            this.completeAnalysis(payload)
            return
          }
          this.analysisStage = 'Análise em processamento'
          this.pollAnalysis(payload.import.uuid, 0)
        })
        .catch(error => {
          const status = error.response?.status
          if ([502, 503, 504].includes(status) || error.code === 'ECONNABORTED') {
            this.failAnalysis('A API não confirmou o recebimento da planilha. Nenhum dado foi importado. Tente novamente.')
            return
          }
          this.failAnalysis(error.response?.data?.message || 'A API não confirmou o recebimento da planilha. Nenhum dado foi importado. Tente novamente.')
        })
    },
    pollAnalysis (uuid, attempt) {
      EnrollmentImportService.get(uuid)
        .then(response => {
          if (this.disposed) return
          const payload = response.data
          if (payload.import.status === 'analyzed') {
            this.completeAnalysis(payload)
            return
          }
          if (payload.import.status === 'failed') {
            this.failAnalysis(payload.import.errorMessage || 'A análise falhou no processamento. Nenhum dado foi importado.')
            return
          }
          if (attempt >= 300) {
            this.failAnalysis('A análise continua sem conclusão após 10 minutos. Nenhum dado foi importado. Tente novamente.')
            return
          }
          this.analysisStage = payload.import.status === 'queued'
            ? 'Análise aguardando processamento'
            : 'Conferindo Apollo, Perseus e Google'
          this.scheduleAnalysisPoll(uuid, attempt + 1)
        })
        .catch(error => {
          if (this.disposed) return
          if (attempt < 300 && (!error.response || [502, 503, 504].includes(error.response.status))) {
            this.analysisStage = 'Reconectando ao acompanhamento da análise'
            this.scheduleAnalysisPoll(uuid, attempt + 1)
            return
          }
          this.failAnalysis(error.response?.data?.message || 'Não foi possível acompanhar a análise. Nenhum dado foi importado.')
        })
    },
    scheduleAnalysisPoll (uuid, attempt) {
      this.clearAnalysisPoll()
      this.analysisPollTimer = window.setTimeout(() => this.pollAnalysis(uuid, attempt), 2000)
    },
    clearAnalysisPoll () {
      if (this.analysisPollTimer) {
        window.clearTimeout(this.analysisPollTimer)
        this.analysisPollTimer = null
      }
    },
    completeAnalysis (payload) {
      this.clearAnalysisPoll()
      this.report = payload
      this.page = 1
      this.analyzing = false
      this.rememberImport(payload.import.uuid)
      if (this.executionInProgress) this.scheduleExecutionPoll(payload.import.uuid, 0)
    },
    rememberImport (uuid) {
      const query = { ...this.$route.query }
      if (uuid) query.import = uuid
      else delete query.import
      this.$router.replace({ query }).catch(() => {})
    },
    failAnalysis (message) {
      this.clearAnalysisPoll()
      this.error = message
      this.analyzing = false
    },
    reset () {
      this.clearAnalysisPoll()
      this.clearExecutionPoll()
      this.selectedFile = null
      this.report = null
      this.analyzing = false
      this.analysisStage = 'Enviando planilha para análise'
      this.error = ''
      this.errorTitle = 'Não foi possível analisar a planilha'
      this.executionError = ''
      this.executionNotice = ''
      this.executionUncertain = false
      this.downloadError = ''
      this.downloadingFeedback = false
      this.executing = false
      this.showExecutionConfirmation = false
      this.canaryOnly = true
      this.retryFailedOnly = false
      this.executionConfirmation = ''
      this.expandedRows = []
      this.clearFilters()
      this.rememberImport(null)
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    setStatus (status) {
      this.filters.status = status
      this.page = 1
    },
    clearFilters () {
      this.filters = { search: '', status: '', classCode: '', issueCode: '' }
      this.page = 1
    },
    toggleRow (row) {
      const index = this.expandedRows.indexOf(row.sourceRow)
      if (index >= 0) this.expandedRows.splice(index, 1)
      else this.expandedRows.push(row.sourceRow)
    },
    isExpanded (row) {
      return this.expandedRows.includes(row.sourceRow)
    },
    openExecutionConfirmation (retryFailed = false) {
      if (!this.report?.executionEnabled || this.executionInProgress || this.executionUncertain) return
      this.retryFailedOnly = retryFailed === true
      if (!this.executionCandidateRows.length) return
      this.canaryOnly = !this.hasExecutionResult
      this.executionConfirmation = ''
      this.executionError = ''
      this.showExecutionConfirmation = true
      this.$nextTick(() => this.$refs.confirmationInput?.focus())
    },
    closeExecutionConfirmation () {
      if (this.executing) return
      this.showExecutionConfirmation = false
      this.executionConfirmation = ''
      this.executionError = ''
      this.$nextTick(() => this.$refs.executionTrigger?.focus())
    },
    handleDialogKeydown (event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeExecutionConfirmation()
      }
      if (event.key !== 'Tab') return
      const controls = [...this.$refs.executionDialog.querySelectorAll('button:not(:disabled), input:not(:disabled)')]
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (!first) {
        event.preventDefault()
        this.$refs.executionDialog.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
    rowExecutionError (row) {
      return row.executionResult?.errorMessage || row.executionResult?.googleAccount?.errorMessage || (row.executionStatus === 'failed' ? 'A integração desta linha falhou. Revise os detalhes antes de tentar novamente.' : '')
    },
    rowExecutionErrorReference (row) {
      return row.executionResult?.errorReference || row.executionResult?.googleAccount?.errorReference
    },
    executeImport () {
      if (this.executing || this.executionInProgress || this.executionUncertain || !this.confirmationMatches) return
      const sourceRows = this.executionSelectionRows.map(row => row.sourceRow)
      this.executing = true
      this.executionError = ''
      EnrollmentImportService.execute(this.report.import.uuid, {
        fileHash: this.report.import.fileHash,
        confirmation: this.expectedConfirmation,
        sourceRows
      })
        .then(response => {
          if (this.disposed) return
          this.executing = false
          this.closeExecutionConfirmation()
          this.report.import = response.data.import
          this.report.executionEnabled = false
          this.report.executionSummary = { requested: sourceRows.length, active: sourceRows.length }
          this.executionNotice = `${sourceRows.length} linha(s) enviada(s). Aguarde a confirmação de conclusão abaixo.`
          this.pollExecution(this.report.import.uuid, 0)
        })
        .catch(error => {
          if (this.disposed) return
          this.executing = false
          if (!error.response || error.response.status >= 500 || error.response.status === 409) {
            this.closeExecutionConfirmation()
            this.executionUncertain = true
            this.executionNotice = 'A API não confirmou o envio. Consultando o lote antes de permitir outra execução.'
            this.pollExecution(this.report.import.uuid, 0)
            return
          }
          this.executionError = error.response?.data?.message || 'Não foi possível enfileirar a execução. Revise a confirmação e tente novamente.'
        })
    },
    refreshExecution () {
      this.clearExecutionPoll()
      this.pollExecution(this.report.import.uuid, 0)
    },
    pollExecution (uuid, attempt) {
      if (this.pollingExecution || this.disposed) return
      this.pollingExecution = true
      return EnrollmentImportService.get(uuid)
        .then(response => {
          if (this.disposed || this.report?.import?.uuid !== uuid) return
          this.report = response.data
          this.executionUncertain = false
          this.executionNotice = ''
          if (!this.executionInProgress) {
            this.clearExecutionPoll()
            return
          }
          if (attempt >= 900) {
            this.executionNotice = 'O acompanhamento automático foi pausado. Use “Atualizar andamento” para consultar a execução, que pode continuar em segundo plano.'
            this.clearExecutionPoll()
            return
          }
          this.scheduleExecutionPoll(uuid, attempt + 1)
        })
        .catch(error => {
          if (this.disposed || this.report?.import?.uuid !== uuid) return
          this.executionNotice = 'Não foi possível atualizar o andamento. A execução pode continuar em segundo plano. Use “Atualizar andamento” para consultar novamente.'
          if (attempt < 900 && (!error.response || [502, 503, 504].includes(error.response.status))) {
            this.executionNotice = 'Conexão interrompida. Reconectando ao andamento da execução; o lote pode continuar em segundo plano.'
            this.scheduleExecutionPoll(uuid, attempt + 1)
            return
          }
          this.clearExecutionPoll()
        })
        .finally(() => { this.pollingExecution = false })
    },
    scheduleExecutionPoll (uuid, attempt) {
      this.clearExecutionPoll()
      this.executionPollTimer = window.setTimeout(() => this.pollExecution(uuid, attempt), 2000)
    },
    clearExecutionPoll () {
      if (this.executionPollTimer) {
        window.clearTimeout(this.executionPollTimer)
        this.executionPollTimer = null
      }
    },
    downloadFeedback () {
      if (this.downloadingFeedback || !this.report?.import?.feedbackAvailable) return
      this.downloadingFeedback = true
      this.downloadError = ''

      EnrollmentImportService.downloadFeedback(this.report.import.uuid)
        .then(response => {
          const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = this.feedbackFileName(response.headers?.['content-disposition'])
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.setTimeout(() => window.URL.revokeObjectURL(url), 0)
        })
        .catch(() => {
          this.downloadError = 'Não foi possível baixar a devolutiva. Reenvie a planilha para gerar uma nova análise.'
        })
        .finally(() => {
          this.downloadingFeedback = false
        })
    },
    feedbackFileName (contentDisposition) {
      const encodedName = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
      if (encodedName) {
        try {
          return decodeURIComponent(encodedName)
        } catch (_) {
          // Usa o nome informado no relatório quando o header vier inválido.
        }
      }

      const simpleName = contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1]
      if (simpleName) return simpleName

      const originalName = this.report?.import?.fileName || 'planilha.xlsx'
      return `${originalName.replace(/\.xlsx$/i, '')}_ANALISE_APOLLO.xlsx`
    },
    executionRowLabel (status) {
      return {
        queued: 'Na fila', processing: 'Criando', submitted: 'Integrando Perseus',
        succeeded: 'Concluída', failed: 'Falhou', skipped: 'Ignorada'
      }[status] || status
    },
    statusLabel (status) {
      return { ready: 'Apto', structure_creatable: 'Criação planejada', already_imported: 'Já importada', blocked: 'Bloqueado' }[status] || status
    },
    issueLabel (code) {
      return issueLabels[code] || code
    },
    structureLabel (type) {
      return { create_google_org_unit: 'Criar organização Google', configure_google_org: 'Configurar organização Google', create_period: 'Criar período', create_series: 'Criar série', link_series: 'Vincular série', create_class: 'Criar turma', link_class: 'Vincular turma' }[type] || type
    },
    creationLabel (action) {
      const labels = {
        create_apollo_person: 'Criar pessoa',
        create_perseus_person: 'Criar pessoa',
        create_apollo_user: 'Criar usuário'
      }
      const base = labels[action.type] || this.structureLabel(action.type) || action.entity
      return action.role ? `${base} · ${this.roleLabel(action.role)}` : base
    },
    roleLabel (role) {
      return { student: 'Aluno', financial: 'Responsável financeiro', pedagogical: 'Responsável pedagógico', parent_1: 'Filiação 1', parent_2: 'Filiação 2' }[role] || role
    },
    traceStatusLabel (status) {
      return { found: 'Encontrado', not_found: 'Não encontrado', candidate: 'Revisar candidato', conflict: 'Conflito', failed: 'Falha técnica' }[status] || status
    },
    issueSummary (row) {
      const issues = [...row.issues, ...this.sharedIssuesForRow(row)]
      if (!issues.length) return 'Sem divergências'
      const errors = issues.filter(issue => issue.severity === 'error').length
      return errors ? `${errors} bloqueio(s)` : `${issues.length} observação(ões)`
    },
    sharedIssuesForRow (row) {
      return this.sharedAnalysis.issues.filter(issue => (issue.appliesToRows || []).includes(row.sourceRow))
    },
    formatRows (rows) {
      const values = rows || []
      if (values.length <= 6) return values.map(row => `L${row}`).join(', ')
      return `${values.slice(0, 5).map(row => `L${row}`).join(', ')} e mais ${values.length - 5}`
    },
    shortHash (hash) {
      return hash ? `${hash.slice(0, 8)}…${hash.slice(-8)}` : '-'
    },
    fileSize (bytes) {
      return `${(bytes / 1024 / 1024).toFixed(2)} MB`
    },
    formatDate (date) {
      return date ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(date)) : '-'
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.enrollment-import-page { padding: 30px; color: #474A4F; }
.page-heading, .section-heading, .section-heading > div, .upload-actions, .analysis-progress, .execution-lock, .execution-panel, .pagination { display: flex; align-items: center; }
.page-heading { justify-content: space-between; gap: 20px; margin-bottom: 30px; }
.eyebrow { display: block; color: #91969C; font-size: 12px; font-weight: 600; margin-bottom: 5px; }
h1 { font-size: 24px; line-height: 32px; margin: 0 0 5px; }
h2 { font-size: 20px; line-height: 28px; margin: 0; }
h3 { font-size: 16px; margin: 0 0 15px; }
p { margin: 0; }
.page-heading p, .section-heading p { color: #73787E; font-size: 13px; }
.analysis-only-badge, .status-badge { border-radius: 999px; font-size: 12px; font-weight: 700; padding: 7px 12px; }
.analysis-only-badge { background: #F3E8FA; color: #7320A2; }
.upload-panel, .summary-section, .results-section { background: #FFF; border: 1px solid #E3E3E9; border-radius: 16px; padding: 25px; margin-bottom: 25px; }
.section-heading { justify-content: space-between; gap: 20px; margin-bottom: 20px; }
.section-heading > div { gap: 15px; }
.step { align-items: center; background: #FDE8E9; border-radius: 999px; color: #94191E; display: inline-flex; flex: 0 0 40px; font-weight: 700; height: 40px; justify-content: center; width: 40px; }
.drop-zone { align-items: center; background: #FAFAFC; border: 1px dashed #CCCCD6; border-radius: 16px; cursor: pointer; display: flex; flex-direction: column; gap: 8px; justify-content: center; min-height: 210px; padding: 30px; text-align: center; transition: border-color 150ms ease, background 150ms ease; }
.drop-zone:hover, .drop-zone:focus-visible, .drop-zone.dragging { background: #FFF7F7; border-color: #ED1C24; outline: none; }
.drop-zone input { height: 1px; opacity: 0; position: absolute; width: 1px; }
.drop-zone > span:last-child { color: #91969C; font-size: 12px; }
.upload-icon { color: #ED1C24; font-size: 32px; }
.upload-actions { justify-content: flex-end; gap: 20px; margin-top: 20px; }
.selected-file { display: flex; flex-direction: column; font-size: 12px; margin-right: auto; }
.selected-file span { color: #91969C; }
.analysis-progress { background: #F6F6F9; border-radius: 14px; gap: 15px; margin-top: 20px; padding: 15px; }
.analysis-progress div { display: flex; flex-direction: column; }
.analysis-progress span { color: #73787E; font-size: 12px; }
.progress-bar { background: #E3E3E9; border-radius: 999px; height: 6px; overflow: hidden; width: 120px; }
.progress-bar i { animation: progress 1.3s ease-in-out infinite; background: #ED1C24; display: block; height: 100%; width: 50%; }
@keyframes progress { from { transform: translateX(-100%); } to { transform: translateX(200%); } }
.state-message { align-items: center; border-radius: 14px; display: flex; justify-content: space-between; margin-top: 20px; padding: 15px; }
.state-message div { display: flex; flex-direction: column; }
.state-message.error { background: #F3E8FA; color: #64188D; }
.summary-grid { display: grid; gap: 15px; grid-template-columns: repeat(5, minmax(0, 1fr)); }
.summary-item { background: #FFF; border: 1px solid #E3E3E9; border-radius: 16px; color: #474A4F; min-height: 130px; padding: 20px; text-align: left; }
.summary-item span, .summary-item small { display: block; }
.summary-item strong { display: block; font-size: 30px; line-height: 38px; margin: 6px 0 3px; }
.summary-item small { color: #91969C; }
.summary-item:hover, .summary-item:focus-visible, .summary-item.active { border-color: #ED1C24; outline: none; }
.summary-item.ready strong { color: #08794A; }
.summary-item.structure strong { color: #126F86; }
.summary-item.imported strong { color: #08794A; }
.summary-item.blocked strong { color: #7320A2; }
.execution-lock { background: #FFF8E1; border: 1px solid #F1D37A; border-radius: 14px; gap: 15px; margin-top: 20px; padding: 15px; }
.execution-lock > span { align-items: center; background: #FFF; border-radius: 999px; display: inline-flex; flex: 0 0 40px; height: 40px; justify-content: center; }
.execution-lock p { color: #6D5A1A; font-size: 12px; }
.execution-panel { background: #F6F6F9; border: 1px solid #E3E3E9; border-radius: 14px; gap: 15px; margin-top: 20px; padding: 15px; }
.completed-panel { background: #F2FBF7; border-color: #B9E8D2; }
.execution-icon { align-items: center; background: #DDF8EC; border-radius: 999px; color: #08794A; display: inline-flex; flex: 0 0 40px; height: 40px; justify-content: center; }
.execution-copy { flex: 1; }
.execution-copy p { color: #73787E; font-size: 12px; margin-top: 2px; }
.execution-copy ul { color: #61666D; font-size: 11px; margin: 8px 0 0; padding-left: 18px; }
.execution-row-status { font-weight: 700; }
.execution-row-status.succeeded { color: #08794A; }
.execution-row-status.failed { color: #94191E; }
.execution-row-status.queued, .execution-row-status.processing, .execution-row-status.submitted { color: #126F86; }
.row-execution-result { background: #F6F6F9; border-radius: 10px; margin-top: 15px; padding: 12px; }
.row-execution-result p { font-size: 12px; margin-top: 4px; }
.row-execution-result small { color: #73787E; }
.execution-error { color: #94191E; }
.execution-actions { display: flex; flex-direction: column; flex-shrink: 0; gap: 8px; }
.execution-notice { background: #FFF8E1; border-radius: 10px; color: #6D5A1A; font-size: 13px; margin-top: 12px; padding: 12px; }
.execution-results { background: #F6F6F9; border: 1px solid #E3E3E9; border-radius: 12px; list-style: none; margin: 12px 0 0; max-height: 240px; overflow-y: auto; padding: 12px; }
.execution-results li { display: flex; flex-direction: column; font-size: 13px; gap: 3px; padding: 6px; }
.enrollment-execution-overlay { align-items: center; background: rgba(31, 33, 36, .55); color: #474A4F; display: flex; inset: 0; isolation: isolate; justify-content: center; opacity: 1; padding: 20px; position: fixed; z-index: 10000; }
.execution-dialog { background: #FFF; border: 1px solid #E3E3E9; border-radius: 16px; box-shadow: 0 24px 70px rgba(31, 33, 36, .25); max-height: calc(100dvh - 40px); max-width: 560px; overflow-y: auto; padding: 25px; width: 100%; }
.execution-dialog :focus-visible { outline: 2px solid #ED1C24; outline-offset: 3px; }
.confirmation-field small { color: #61666D; font-size: 12px; }
.execution-dialog h2 { margin: 3px 0 8px; }
.execution-dialog > p { color: #73787E; font-size: 13px; }
.dialog-kicker { color: #94191E; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.canary-option { align-items: flex-start; background: #FFF8E1; border: 1px solid #F1D37A; border-radius: 12px; cursor: pointer; display: flex; gap: 10px; margin: 20px 0; padding: 12px; }
.canary-option input { height: 18px; margin-top: 1px; width: 18px; }
.canary-option span, .canary-option small { display: block; }
.canary-option small { color: #6D5A1A; font-size: 11px; margin-top: 3px; }
.confirmation-summary { background: #F6F6F9; border-radius: 12px; display: grid; font-size: 12px; gap: 8px 15px; grid-template-columns: 1fr auto; margin-bottom: 18px; padding: 14px; }
.confirmation-summary span { color: #73787E; }
.confirmation-field { display: flex; flex-direction: column; gap: 7px; }
.confirmation-field > span { font-size: 12px; }
.confirmation-field input { border: 1px solid #CCCCD6; border-radius: 12px; height: 44px; padding: 0 14px; }
.confirmation-field input:focus { border-color: #ED1C24; outline: 2px solid rgba(237, 28, 36, .12); }
.dialog-error { background: #F3E8FA; border-radius: 10px; color: #64188D; font-size: 12px; margin-top: 12px; padding: 10px; }
.dialog-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.shared-analysis { background: #FAFAFC; border: 1px solid #E3E3E9; border-radius: 16px; margin-top: 20px; padding: 20px; }
.shared-heading { align-items: flex-start; display: flex; gap: 20px; justify-content: space-between; margin-bottom: 20px; }
.shared-heading h3 { margin: 2px 0 4px; }
.shared-heading p { color: #73787E; font-size: 12px; }
.shared-kicker { color: #94191E; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.shared-count { background: #FDE8E9; border-radius: 999px; color: #94191E; flex: 0 0 auto; font-size: 11px; font-weight: 700; padding: 7px 10px; }
.shared-grid { display: grid; gap: 20px; grid-template-columns: repeat(3, minmax(0, 1fr)); }
.shared-grid h4 { font-size: 13px; margin: 0 0 10px; }
.shared-list { max-height: 520px; overflow: auto; padding-right: 3px; }
.affected-rows, .scope-label { color: #91969C; display: block; font-size: 10px; margin-top: 8px; }
.analyzed-at { color: #73787E; font-size: 12px; }
.analysis-meta { align-items: flex-end; display: flex; flex-direction: column; gap: 6px; text-align: right; }
.feedback-download { align-items: center; display: inline-flex; gap: 8px; min-height: 44px; }
.feedback-note { color: #73787E; font-size: 12px; max-width: 290px; }
.feedback-error { color: #7320A2; font-size: 12px; max-width: 320px; }
.filters { align-items: end; display: grid; gap: 15px; grid-template-columns: 2fr repeat(3, 1fr) auto; margin-bottom: 20px; }
.filters label { display: flex; flex-direction: column; gap: 7px; }
.filters label > span { font-size: 12px; font-weight: 600; }
.filters input, .filters select { background: #FFF; border: 1px solid #CCCCD6; border-radius: 14px; color: #474A4F; height: 44px; padding: 0 14px; width: 100%; }
.filters input:focus, .filters select:focus { border-color: #ED1C24; outline: 2px solid rgba(237, 28, 36, .12); }
.table-wrap { overflow-x: auto; }
table { border-collapse: collapse; width: 100%; }
th { color: #73787E; font-size: 12px; font-weight: 700; padding: 12px 10px; text-align: left; }
td { border-top: 1px solid #ECECF1; padding: 14px 10px; vertical-align: middle; }
td strong, td small { display: block; }
td small { color: #91969C; font-size: 12px; margin-top: 3px; }
.status-badge.ready { background: #DDF8EC; color: #08794A; }
.status-badge.structure_creatable { background: #E1F7FB; color: #126F86; }
.status-badge.already_imported { background: #DDF8EC; color: #08794A; }
.status-badge.blocked { background: #F3E8FA; color: #7320A2; }
.details-cell { text-align: right; }
.detail-button { background: transparent; border: 0; color: #94191E; font-weight: 700; min-height: 40px; padding: 0 8px; }
.detail-button:focus-visible { outline: 2px solid #ED1C24; outline-offset: 2px; }
.detail-row > td { background: #FAFAFC; padding: 20px; }
.detail-grid { display: grid; gap: 20px; grid-template-columns: repeat(3, minmax(0, 1fr)); }
.detail-section { min-width: 0; }
.detail-heading { align-items: flex-start; display: flex; gap: 10px; margin-bottom: 15px; }
.detail-heading h3 { margin: 0; }
.detail-heading p { color: #91969C; font-size: 12px; margin-top: 3px; }
.detail-step { align-items: center; background: #FDE8E9; border-radius: 999px; color: #94191E; display: inline-flex; flex: 0 0 28px; font-size: 12px; font-weight: 700; height: 28px; justify-content: center; }
.issue-list, .structure-list, .trace-list { display: grid; gap: 10px; list-style: none; margin: 0; padding: 0; }
.issue-list > li, .structure-list > li, .trace-list > li { background: #FFF; border: 1px solid #E3E3E9; border-left: 4px solid #91969C; border-radius: 6px 14px 14px 6px; padding: 12px 15px; }
.issue-list li.error { border-left-color: #A51EED; }
.issue-list li.info { border-left-color: #1DCDED; }
.issue-list li.warning { border-left-color: #D99700; }
.issue-list span, .structure-list strong { font-size: 13px; font-weight: 700; }
.issue-list p, .structure-list p { font-size: 13px; margin: 4px 0; }
.issue-title, .trace-header, .action-heading { align-items: flex-start; display: flex; gap: 8px; justify-content: space-between; }
.issue-title small, .trace-header small { color: #91969C; display: block; font-size: 11px; font-weight: 400; margin-top: 2px; }
.resolution { background: #F6F6F9; border-radius: 8px; color: #565A60; padding: 8px 10px; }
.trace-header strong { font-size: 13px; }
.trace-status { border-radius: 999px; flex: 0 0 auto; font-size: 10px; font-weight: 700; padding: 4px 7px; }
.trace-status.found { background: #DDF8EC; color: #08794A; }
.trace-status.not_found { background: #F1F1F4; color: #61666D; }
.trace-status.candidate, .trace-status.conflict, .trace-status.failed { background: #F3E8FA; color: #7320A2; }
.trace-list > li { border-left-color: #CCCCD6; }
.trace-list ul, .prerequisites ul { color: #73787E; font-size: 11px; margin: 8px 0; padding-left: 18px; }
.trace-list p { border-top: 1px solid #ECECF1; font-size: 12px; margin-top: 8px; padding-top: 8px; }
.action-heading { justify-content: flex-start; }
.action-heading > span { align-items: center; background: #F1F1F4; border-radius: 999px; display: inline-flex; flex: 0 0 24px; font-size: 11px; height: 24px; justify-content: center; }
.prerequisites { border-top: 1px solid #ECECF1; margin-top: 10px; padding-top: 8px; }
.prerequisites small { color: #73787E; font-size: 11px; font-weight: 700; }
.prerequisites ul { margin-bottom: 0; }
.no-issues, .empty-state { color: #73787E; font-size: 13px; }
.empty-state { align-items: center; display: flex; flex-direction: column; gap: 5px; padding: 50px 20px; text-align: center; }
.pagination { justify-content: center; gap: 15px; margin-top: 20px; }
.sr-only { clip: rect(0, 0, 0, 0); clip-path: inset(50%); height: 1px; overflow: hidden; position: absolute; white-space: nowrap; width: 1px; }
@media (max-width: 1100px) { .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .shared-grid { grid-template-columns: 1fr; } .filters { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 720px) { .enrollment-import-page { padding: 15px; } .page-heading, .section-heading, .shared-heading, .execution-panel { align-items: flex-start; flex-direction: column; } .upload-panel, .summary-section, .results-section { padding: 20px 15px; } .summary-grid, .filters, .detail-grid { grid-template-columns: 1fr; } .upload-actions { align-items: stretch; flex-direction: column; } .upload-actions .btn, .execution-panel .btn { width: 100%; } .analysis-meta { align-items: stretch; text-align: left; width: 100%; } .feedback-download { justify-content: center; width: 100%; } .feedback-note, .feedback-error { max-width: none; } .dialog-actions { flex-direction: column-reverse; } .dialog-actions .btn { width: 100%; } th:nth-child(3), td:nth-child(3), th:nth-child(4), td:nth-child(4) { display: none; } .detail-row td { display: table-cell; } }
@media (prefers-reduced-motion: reduce) { .progress-bar i { animation: none; width: 100%; } }
</style>
